import { useCallback, useEffect, useRef, useState } from 'react';
import { founderApi } from '@/lib/founderApi';

const DEBOUNCE = 1200;
const RETRIES = [2000, 4000, 8000];
const TAB_ID = sessionStorage.getItem('fbp-tab') || Math.random().toString(36).slice(2);
sessionStorage.setItem('fbp-tab', TAB_ID);
const prefix = (bid, stage) => `fbp-unsaved:${bid}:${stage}:`;
const backupKey = (bid, stage) => `${prefix(bid, stage)}${TAB_ID}`;
// Adopt unsaved backups left by closed tabs or earlier sessions for this stage.
const adoptBackups = (bid, stage) => {
  let merged = {};
  Object.keys(localStorage).filter((k) => k.startsWith(prefix(bid, stage))).sort((a) => (a === backupKey(bid, stage) ? 1 : -1)).forEach((k) => {
    try { merged = { ...merged, ...JSON.parse(localStorage.getItem(k)) }; } catch { /* ignore corrupt backup */ }
    localStorage.removeItem(k);
  });
  return merged;
};

// Debounced, versioned autosave for one stage. Unsaved edits are mirrored to localStorage until the server confirms.
export function useStageAutosave({ bid, stage, versionRef, onSaved }) {
  const [dirty, setDirty] = useState({});
  const [status, setStatus] = useState({ state: 'idle' });
  const dirtyRef = useRef({});
  const timer = useRef(null);
  const inflight = useRef(null);
  const again = useRef(false);
  const attempt = useRef(0);

  const persist = useCallback((next) => {
    dirtyRef.current = next;
    setDirty(next);
    if (Object.keys(next).length) localStorage.setItem(backupKey(bid, stage), JSON.stringify(next));
    else localStorage.removeItem(backupKey(bid, stage));
  }, [bid, stage]);

  const send = useCallback(async () => {
    const snapshot = { ...dirtyRef.current };
    if (!Object.keys(snapshot).length) return;
    if (!navigator.onLine) { setStatus({ state: 'offline' }); return; }
    setStatus({ state: 'saving' });
    try {
      const view = await founderApi.saveStage(bid, stage, versionRef.current, snapshot);
      versionRef.current = view.version;
      attempt.current = 0;
      const rest = { ...dirtyRef.current };
      Object.keys(snapshot).forEach((k) => { if (rest[k] === snapshot[k]) delete rest[k]; });
      persist(rest);
      onSaved(view);
      setStatus({ state: 'saved', at: new Date() });
    } catch (e) {
      const code = e?.response?.status;
      if (code === 409) { setStatus({ state: 'conflict' }); return; }
      if (code === 422 || code === 400 || code === 404 || code === 401) {
        setStatus({ state: 'error', message: code === 401 ? 'Your session ended. Please sign in again. Your changes are kept on this device.' : 'Some answers could not be saved. Please review them.', fields: e.response?.data?.detail?.fields });
        return;
      }
      if (attempt.current < RETRIES.length) {
        const wait = RETRIES[attempt.current];
        attempt.current += 1;
        setStatus({ state: 'retrying' });
        timer.current = setTimeout(() => flush(), wait); // eslint-disable-line no-use-before-define
        return;
      }
      attempt.current = 0;
      setStatus({ state: 'error', message: 'We couldn\u2019t reach the server. Your changes are kept on this device.' });
    }
  }, [bid, stage, versionRef, onSaved, persist]); // eslint-disable-line react-hooks/exhaustive-deps

  const flush = useCallback(async () => {
    clearTimeout(timer.current);
    if (inflight.current) { again.current = true; return inflight.current; }
    inflight.current = send().finally(() => { inflight.current = null; });
    await inflight.current;
    if (again.current) { again.current = false; await flush(); }
    return undefined;
  }, [send]);

  const queue = useCallback((key, value) => {
    persist({ ...dirtyRef.current, [key]: value });
    setStatus((s) => (s.state === 'conflict' ? s : { state: 'pending' }));
    clearTimeout(timer.current);
    timer.current = setTimeout(() => flush(), DEBOUNCE);
  }, [flush, persist]);

  const discard = useCallback(() => { persist({}); setStatus({ state: 'idle' }); }, [persist]);

  useEffect(() => {
    const restored = adoptBackups(bid, stage);
    if (Object.keys(restored).length) localStorage.setItem(backupKey(bid, stage), JSON.stringify(restored));
    dirtyRef.current = restored;
    setDirty(restored);
    setStatus(Object.keys(restored).length ? { state: 'pending', restored: true } : { state: 'idle' });
    if (Object.keys(restored).length) timer.current = setTimeout(() => flush(), 300);
    const online = () => flush();
    const warn = (ev) => { if (Object.keys(dirtyRef.current).length) { ev.preventDefault(); ev.returnValue = ''; } };
    window.addEventListener('online', online);
    window.addEventListener('beforeunload', warn);
    return () => {
      clearTimeout(timer.current);
      window.removeEventListener('online', online);
      window.removeEventListener('beforeunload', warn);
    };
  }, [bid, stage]); // eslint-disable-line react-hooks/exhaustive-deps

  return { dirty, status, queue, flush, discard };
}
